import {inject, Injectable} from '@angular/core';
import {Observable} from 'rxjs';
import {StateControllerService} from '../../api';
import {ChatStreamEvent} from './chat-stream.model';
import {TokenRefreshService} from '../auth/token-refresh.service';

/**
 * Liest den Antwort-Stream eines Chat-Turns.
 *
 * <p>Aufgebaut wie {@link SseService}: `fetch()` statt `EventSource`, weil nur so der
 * `Authorization`-Header mitgeht. Zwei Unterschiede, beide beabsichtigt:
 *
 * <ul>
 *   <li><b>POST statt GET.</b> Frage und Stream laufen über <em>denselben</em> Request. Damit liegen
 *       Verbindung und Agent zwangsläufig auf derselben Replika – ohne Sticky Sessions und ohne
 *       Umweg über eine Nachrichtenwarteschlange.</li>
 *   <li><b>Kein automatischer Reconnect.</b> Der {@link SseService} verbindet sich bei Abbruch neu;
 *       das wäre hier falsch, denn ein erneuter POST würde die Frage ein zweites Mal stellen. Bricht
 *       der Stream ab, läuft der Turn serverseitig weiter – die Antwort holt man über die Nachricht
 *       ab, nicht durch Wiederholen.</li>
 * </ul>
 */
@Injectable({providedIn: 'root'})
export class ChatStreamService {
  private stateControllerService = inject(StateControllerService);
  private tokenRefresh = inject(TokenRefreshService);

  private get basePath(): string {
    return (this.stateControllerService as any).basePath ?? '';
  }

  /**
   * Stellt eine Frage und liefert die Ereignisse der Antwort.
   *
   * <p>Das Abbestellen des Observables bricht nur die <em>Verbindung</em> ab, nicht den Turn.
   */
  ask(threadId: string, body: unknown): Observable<ChatStreamEvent> {
    return new Observable<ChatStreamEvent>(observer => {
      const abortController = new AbortController();

      (async () => {
        try {
          // Ein 401 kommt aus dem Security-Filter, bevor der Turn angelegt ist – die Wiederholung in
          // fetchWithToken stellt die Frage also nicht doppelt.
          const response = await this.fetchWithToken(`${this.basePath}/chat/threads/${threadId}/turns`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Accept': 'text/event-stream'
            },
            body: JSON.stringify(body),
            signal: abortController.signal
          });

          if (response.status === 409) {
            // Der Server nennt den laufenden Turn. Damit kann sich die Oberfläche anhängen, statt
            // einen Fehler zu zeigen – siehe `follow()`.
            let turnId: string | undefined;
            try {
              turnId = (await response.json())?.turnId ?? undefined;
            } catch {
              // Ohne ID bleibt es beim Hinweis.
            }
            observer.error({
              code: 'turn_running',
              turnId,
              message: 'In diesem Chat läuft noch eine Antwort.'
            });
            return;
          }
          if (response.status === 402) {
            // Das Kontingent des Projekts ist ausgeschöpft. Der Rumpf trägt den Stand, damit die
            // Oberfläche die Sperre begründen kann – die Frage wurde nicht gesendet.
            let quota: unknown;
            try {
              quota = await response.json();
            } catch {
              // Ohne Stand bleibt es beim Hinweis.
            }
            observer.error({
              code: 'chat_quota_exceeded',
              quota,
              message: 'chatBIC-Kontingent aufgebraucht.'
            });
            return;
          }
          if (!response.ok || !response.body) {
            observer.error({
              code: 'http_' + response.status,
              message: `Chat-Stream fehlgeschlagen: ${response.status}`
            });
            return;
          }

          const reader = response.body.getReader();
          const decoder = new TextDecoder();
          let buffer = '';

          while (true) {
            const {done, value} = await reader.read();
            if (done) break;

            buffer += decoder.decode(value, {stream: true});
            const lines = buffer.split('\n');
            // Die letzte Zeile kann abgeschnitten sein – sie bleibt im Puffer, bis sie vollständig
            // ist. Ohne das zerreisst ein Chunk mitten in einem JSON-Objekt die Antwort.
            buffer = lines.pop() ?? '';

            for (const line of lines) {
              if (!line.startsWith('data:')) {
                continue; // Heartbeats kommen als Kommentarzeile (":ping") und tragen keine Daten
              }
              const payload = line.slice(5).trim();
              if (!payload) continue;
              try {
                observer.next(JSON.parse(payload) as ChatStreamEvent);
              } catch {
                // Unvollständiges JSON verwerfen statt den ganzen Stream abzubrechen.
              }
            }
          }
          observer.complete();
        } catch (err: any) {
          if (err?.name === 'AbortError') {
            observer.complete();
            return;
          }
          observer.error({code: 'network', message: err?.message ?? 'Verbindung unterbrochen'});
        }
      })();

      return () => abortController.abort();
    });
  }

  /**
   * Hängt sich an eine bereits laufende Antwort an.
   *
   * <p>Gegenstück zum 409 aus {@link ask}: dort wurde nichts gesendet, hier wird nur zugehört. Der
   * Server schickt zuerst die schon protokollierten Ereignisse (Schritte, UI-Blöcke), dann läuft es
   * live weiter.
   *
   * <p><b>Die Token der Vergangenheit fehlen</b> – sie werden serverseitig nicht protokolliert. Der
   * vollständige Text kommt mit dem `done`-Ereignis, die Anzeige korrigiert sich also am Ende
   * selbst.
   */
  /**
   * „Neu generieren": die letzte Antwort verwerfen und dieselbe Frage neu beantworten. Streamt wie
   * {@link ask}; die alte Antwort ist danach `SUPERSEDED`.
   */
  regenerate(threadId: string, messageId: string, mode: string): Observable<ChatStreamEvent> {
    return this.stream(
      `${this.basePath}/chat/threads/${threadId}/messages/${messageId}/regenerate?mode=${encodeURIComponent(mode)}`,
      {method: 'POST'}
    );
  }

  follow(turnId: string, fromSeq = 0): Observable<ChatStreamEvent> {
    return this.stream(
      `${this.basePath}/chat/turns/${turnId}/follow?fromSeq=${fromSeq}`,
      {method: 'GET'}
    );
  }

  /**
   * Meldet das Ergebnis eines Viewer-Aufrufs an den wartenden Turn.
   *
   * <p>Darf auf jeder Replika landen – das Backend übergibt über die Datenbank. 409 heißt: schon
   * beantwortet (anderer Tab) oder der Turn wartet nicht mehr; beides ist hier kein Fehler.
   */
  async answerClientCall(turnId: string, callId: string,
                         body: { ok: boolean; result?: unknown; error?: string }): Promise<void> {
    const response = await this.fetchWithToken(`${this.basePath}/chat/turns/${turnId}/client-calls/${callId}`, {
      method: 'POST',
      headers: {'Content-Type': 'application/json'},
      body: JSON.stringify(body),
    });
    if (response.ok || response.status === 409) return;
    throw new Error(`Viewer-Ergebnis abgelehnt: ${response.status}`);
  }

  /**
   * `fetch` mit frischem Access-Token.
   *
   * <p>Diese Aufrufe laufen an den HttpClient-Interceptors vorbei, also auch am
   * {@link AuthInterceptor}. Deshalb hier dasselbe: vorher ein gültiges Token holen und nach einem
   * 401 einmal mit erneuertem Token wiederholen. Das zweite 401 geht an den Aufrufer (`http_401`).
   * Ohne das scheiterte etwa die Viewer-Rückmeldung eines laufenden Turns, sobald das Token
   * zwischen Frage und Rückmeldung abläuft.
   *
   * @param init darf keinen `Authorization`-Header enthalten, und `body` muss wiederverwendbar sein
   *             (String, kein Stream).
   */
  private async fetchWithToken(url: string, init: RequestInit & {headers: Record<string, string>}): Promise<Response> {
    const token = await this.tokenRefresh.getValidToken();
    const response = await fetch(url, this.withToken(init, token));
    if (response.status !== 401 || !token) {
      return response;
    }
    const fresh = await this.tokenRefresh.refreshAfterUnauthorized(token);
    if (!fresh) {
      return response;
    }
    return fetch(url, this.withToken(init, fresh));
  }

  private withToken(init: RequestInit & {headers: Record<string, string>}, token: string | null): RequestInit {
    return {...init, headers: {...init.headers, 'Authorization': `Bearer ${token ?? ''}`}};
  }

  /** Liest einen SSE-Stream und gibt die Ereignisse einzeln weiter. */
  private stream(url: string, init: RequestInit): Observable<ChatStreamEvent> {
    return new Observable<ChatStreamEvent>(observer => {
      const abortController = new AbortController();

      (async () => {
        try {
          const response = await this.fetchWithToken(url, {
            ...init,
            headers: {
              'Accept': 'text/event-stream'
            },
            signal: abortController.signal
          });

          if (!response.ok || !response.body) {
            observer.error({
              code: 'http_' + response.status,
              message: `Chat-Stream fehlgeschlagen: ${response.status}`
            });
            return;
          }

          const reader = response.body.getReader();
          const decoder = new TextDecoder();
          let buffer = '';

          while (true) {
            const {done, value} = await reader.read();
            if (done) break;

            buffer += decoder.decode(value, {stream: true});
            const lines = buffer.split('\n');
            buffer = lines.pop() ?? '';

            for (const line of lines) {
              if (!line.startsWith('data:')) continue;
              const payload = line.slice(5).trim();
              if (!payload) continue;
              try {
                observer.next(JSON.parse(payload) as ChatStreamEvent);
              } catch {
                // Unvollständiges JSON verwerfen statt den ganzen Stream abzubrechen.
              }
            }
          }
          observer.complete();
        } catch (err: any) {
          if (err?.name === 'AbortError') {
            observer.complete();
            return;
          }
          observer.error({code: 'network', message: err?.message ?? 'Verbindung unterbrochen'});
        }
      })();

      return () => abortController.abort();
    });
  }
}
